import { AsyncPipe, DOCUMENT, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  EnvironmentInjector,
  NgZone,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  createNgModule,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';

import { Observable, map, mergeMap, take } from 'rxjs';

import { RouteData } from '@hdms/shared/common-types';
import { PageFooterComponent, SharedPageFrameFeatureModule } from '@hdms/shared/page-frame/feature';
import { PreloaderModule } from '@hdms/shared/ui-core';
import {
  AnalyticsService,
  EnvironmentService,
  FeatureToggleService,
  FrontendMode,
  HostContext,
  PageConfigService,
  PlatformService,
  RouterHelperService,
  RouterPageTitleService
} from '@hdms/shared/util-core';

import { AppPortalService } from '../services/app-portal.service';

export const HDMS_CONTENT_CLS = 'hdms-content';
export const PREVENT_TRANS_CLS = 'prevent-transition';
export const MOBILE_APP_CLS = 'is-mobile-app';
export const COMPRESSED_LAYOUT_CLS = 'is-compressed-page-layout';

@Component({
  selector: 'website-root',
  templateUrl: './app.component.html',
  // TODO: re-evaluate what's required for enabling onPush in app component,
  //  in order to reduce permanent CD invocation on click, scroll etc.
  //  Right now, enabling onPush will screw up detection of view changes
  //  in some areas where implementation doesn't fully rely on input
  //  bindings or CD isn't triggered manually.
  // changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AppPortalService],
  imports: [NgIf, NgTemplateOutlet, RouterOutlet, PreloaderModule, AsyncPipe, SharedPageFrameFeatureModule],
  standalone: true
})
export class AppComponent implements OnInit, AfterViewInit {
  @ViewChild('footerRef', { read: ViewContainerRef, static: true })
  private readonly footerRef: ViewContainerRef;
  @ViewChild('defaultOutlet', { read: TemplateRef, static: true })
  private readonly defaultOutlet: TemplateRef<HTMLElement>;
  @ViewChild('globalToastContainerRef', { read: ViewContainerRef })
  private readonly globalToastContainerRef: ViewContainerRef;

  readonly isStandalone: boolean;
  readonly isMobileApp: boolean;
  readonly hostContext: HostContext;
  readonly qaI18nNsData$: Observable<string>;

  private readonly pageConfigService = inject(PageConfigService);
  private readonly document = inject(DOCUMENT);
  private readonly routerHelper = inject(RouterHelperService);
  private readonly platform = inject(PlatformService);
  private readonly env = inject(EnvironmentService);
  private readonly renderer = inject(Renderer2);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly portalService = inject(AppPortalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(EnvironmentInjector);
  private readonly zone = inject(NgZone);

  constructor() {
    this.isStandalone = this.env.isStandalone;
    this.isMobileApp = this.env.mobileApp;
    this.hostContext = this.env.hostContext;
    this.qaI18nNsData$ = this.getQaI18nNsData$();

    this.instantiateServices();
  }

  ngOnInit(): void {
    !this.hostContext && this.initPageConfig();
    this.initPageFrame();
    this.updateBodyClasses();
  }

  ngAfterViewInit(): void {
    this.portalService.configureToastContainer(this.globalToastContainerRef);
  }

  onOutletActivate(outletElement: HTMLElement): void {
    const outletParent = outletElement.parentElement;
    Array.from(outletParent.children).forEach(node => {
      if (node !== outletElement) {
        this.renderer.addClass(node, HDMS_CONTENT_CLS);
      }
    });
  }

  /**
   * Below services just have to be instantiated, to do their job
   */
  private instantiateServices(): void {
    inject(FeatureToggleService);
    inject(AnalyticsService);

    if (this.hostContext !== HostContext.AEM && this.env.frontendMode !== FrontendMode.NONE) {
      inject(RouterPageTitleService);
    }
  }

  private initPageConfig(): void {
    this.pageConfigService.init();
    this.pageConfigService.isCompressedPageLayout$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(compressed => this.toggleClass(COMPRESSED_LAYOUT_CLS, compressed));
  }

  private initPageFrame(): void {
    if (!this.isMobileApp) {
      const ngModuleRef = createNgModule(SharedPageFrameFeatureModule, this.injector);
      if (this.hostContext) {
        this.portalService.renderFooterPortal(PageFooterComponent, ngModuleRef);
      } else {
        this.footerRef.createComponent(PageFooterComponent, { ngModuleRef });
      }
    }

    this.portalService.renderContentPortal(this.defaultOutlet, this.viewContainerRef);
  }

  private updateBodyClasses(): void {
    this.unblockCssTransitions();
    this.isMobileApp && this.toggleClass(MOBILE_APP_CLS, true);
  }

  /**
   * "prevent-transition" class is initially applied to body tag, which disables any CSS transitions,
   * because otherwise they will be run on page initialization due to repainting caused by DOM rehydration.
   * As soon app is stable this CSS class has to be removed in order to re-enable any CSS transitions/animations.
   * @private
   */
  private unblockCssTransitions(): void {
    this.platform.isBrowser &&
      this.zone.onStable.pipe(take(1)).subscribe(() => this.toggleClass(PREVENT_TRANS_CLS, false));
  }

  private toggleClass(cls: string, state: boolean): void {
    this.renderer[state ? 'addClass' : 'removeClass'](this.document.body, cls);
  }

  /**
   * Test-Automation requires current feature namespace(s) applied
   * somewhere in the DOM. The below observable creates a json string
   * containing the i18n feature namespaces.
   * The observable will be executed only in ui-test scenario.
   * @private
   */
  private getQaI18nNsData$(): Observable<string> {
    return this.routerHelper.activatedRoute$.pipe(
      mergeMap(route => route.data),
      map<RouteData, string>(({ i18nextNamespaces }) => JSON.stringify({ i18nNs: i18nextNamespaces }))
    );
  }
}
